import type {
  CampaignReportRow,
  CreatorPerformanceRow,
} from '@/types/portal';
import { formatCompact, formatMoney } from './format';
import { toKesEquivalent } from './invoice';

export type ReportPeriod = '3m' | '6m' | 'ytd';

export interface ReportKpi {
  label: string;
  value: string;
  icon: string;
  tone: 'primary' | 'accent' | 'secondary' | 'neutral';
  hint?: string;
  delta?: string;
  deltaPositive?: boolean;
}

function varianceLabel(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function campaignKpis(rows: CampaignReportRow[]): ReportKpi[] {
  const budget = rows.reduce((sum, row) => sum + toKesEquivalent(row.budget, row.currency), 0);
  const spend = rows.reduce((sum, row) => sum + toKesEquivalent(row.spend, row.currency), 0);
  const reach = rows.reduce((sum, row) => sum + row.reach, 0);
  const engagements = rows.reduce((sum, row) => sum + row.engagements, 0);
  const engagementRate = reach > 0 ? (engagements / reach) * 100 : 0;
  const inFlight = rows.filter((row) =>
    ['Active', 'Recruiting', 'Review', 'Paying'].includes(row.status),
  ).length;
  const variance = budget > 0 ? ((spend - budget) / budget) * 100 : 0;

  return [
    {
      label: 'Campaigns',
      value: String(rows.length),
      icon: 'ri-megaphone-line',
      tone: 'primary',
      hint: `${inFlight} in flight`,
    },
    {
      label: 'Total budget',
      value: formatMoney(budget, 'KES'),
      icon: 'ri-wallet-3-line',
      tone: 'secondary',
      hint: 'KES equivalent',
    },
    {
      label: 'Total spend',
      value: formatMoney(spend, 'KES'),
      icon: 'ri-line-chart-line',
      tone: 'accent',
      delta: varianceLabel(variance),
      deltaPositive: variance <= 0,
      hint: 'vs planned budget',
    },
    {
      label: 'Blended engagement',
      value: `${engagementRate.toFixed(1)}%`,
      icon: 'ri-heart-3-line',
      tone: 'neutral',
      hint: `${formatCompact(reach)} reach`,
    },
  ];
}

export function creatorKpis(rows: CreatorPerformanceRow[]): ReportKpi[] {
  const paid = rows.reduce((sum, row) => sum + toKesEquivalent(row.paid, row.currency), 0);
  const reach = rows.reduce((sum, row) => sum + row.reach, 0);
  const engagementRate = rows.length
    ? rows.reduce((sum, row) => sum + row.engagementRate, 0) / rows.length
    : 0;
  const onTime = rows.length
    ? rows.reduce((sum, row) => sum + row.onTimeRate, 0) / rows.length
    : 0;

  return [
    {
      label: 'Creators tracked',
      value: String(rows.length),
      icon: 'ri-user-star-line',
      tone: 'primary',
      hint: 'With settled or in-flight work',
    },
    {
      label: 'Creator spend',
      value: formatMoney(paid, 'KES'),
      icon: 'ri-hand-coin-line',
      tone: 'accent',
      hint: 'KES equivalent',
    },
    {
      label: 'Avg engagement',
      value: `${engagementRate.toFixed(1)}%`,
      icon: 'ri-heart-3-line',
      tone: 'secondary',
      hint: 'Across the cohort',
    },
    {
      label: 'Avg on-time delivery',
      value: `${onTime.toFixed(0)}%`,
      icon: 'ri-timer-line',
      tone: 'neutral',
      hint: `${formatCompact(reach)} combined reach`,
    },
  ];
}

function csvCell(value: string | number): string {
  const text = String(value);
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function toCsv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export function campaignCsv(rows: CampaignReportRow[]): string {
  return toCsv(
    [
      'Campaign',
      'Client',
      'Markets',
      'Currency',
      'Budget',
      'Spend',
      'Creators',
      'Reach',
      'Engagements',
      'Engagement rate %',
      'Status',
    ],
    rows.map((row) => [
      row.name,
      row.brandClient,
      row.markets.join(' / '),
      row.currency,
      row.budget,
      row.spend,
      row.creators,
      row.reach,
      row.engagements,
      row.engagementRate,
      row.status,
    ]),
  );
}

export function creatorCsv(rows: CreatorPerformanceRow[]): string {
  return toCsv(
    [
      'Creator',
      'Campaign',
      'Market',
      'Platform',
      'Tier',
      'Followers',
      'Reach',
      'Engagement rate %',
      'Deliverables',
      'On-time %',
      'Paid',
      'Currency',
      'Rating',
      'Trend',
    ],
    rows.map((row) => [
      row.name,
      row.campaign,
      row.market,
      row.platform,
      row.tier,
      row.followers,
      row.reach,
      row.engagementRate,
      row.deliverables,
      row.onTimeRate,
      row.paid,
      row.currency,
      row.rating,
      row.trend,
    ]),
  );
}

/** Trigger a browser download for a generated CSV payload. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
export interface ReportInsight {
  title: string;
  body: string;
  bullets: string[];
}

const pct = (value: number) => `${Math.round(value)}%`;

/**
 * The campaign "AI summary", worked out from the rows (there is no AI or
 * Laravel equivalent yet): where spend is concentrated and pacing against plan.
 */
export function campaignInsight(rows: CampaignReportRow[]): ReportInsight {
  const spend = rows.reduce((sum, row) => sum + toKesEquivalent(row.spend, row.currency), 0);
  const reach = rows.reduce((sum, row) => sum + row.reach, 0);
  const engagements = rows.reduce((sum, row) => sum + row.engagements, 0);
  const blended = reach > 0 ? `Blended engagement is ${((engagements / reach) * 100).toFixed(1)}% across ${formatCompact(reach)} reach.` : 'No approved content has synced performance yet.';
  // Campaigns with money or content moving first; drafts have nothing to pace.
  const pacing = rows
    .filter((row) => row.budget > 0 && row.status !== 'Draft')
    .sort((a, b) => b.spend / b.budget - a.spend / a.budget || b.delivered - a.delivered)
    .map((row) => {
      const delivered = row.totalDeliverables > 0 ? pct((row.delivered / row.totalDeliverables) * 100) : '0%';
      const used = (row.spend / row.budget) * 100;
      return used > 100
        ? `${row.name} is ${pct(used - 100)} over plan with ${delivered} of deliverables in`
        : `${row.name} has deployed ${pct(used)} of budget with ${delivered} of deliverables in`;
    })
    .slice(0, 3);

  const bySpend = [...rows].sort(
    (a, b) => toKesEquivalent(b.spend, b.currency) - toKesEquivalent(a.spend, a.currency),
  );
  const leaders = bySpend.filter((row) => row.spend > 0).slice(0, 2);
  if (spend === 0 || leaders.length === 0) {
    return {
      title: rows.length ? 'No creator payouts recorded this period' : 'No campaigns in this period',
      body: rows.length
        ? `Spend appears as creator payouts complete. ${blended}`
        : 'Widen the reporting period or market filter to see campaign performance.',
      bullets: pacing,
    };
  }
  const share = (leaders.reduce((sum, row) => sum + toKesEquivalent(row.spend, row.currency), 0) / spend) * 100;
  const names = leaders.map((row) => row.name).join(' and ');
  return {
    title: leaders.length > 1 ? `Two campaigns are carrying ${pct(share)} of spend` : `${leaders[0].name} is carrying ${pct(share)} of spend`,
    body: `${names} ${leaders.length > 1 ? 'account' : 'accounts'} for ${pct(share)} of spend this period. ${blended}`,
    bullets: pacing,
  };
}

/** The creator "AI summary": who leads on engagement and how tiers compare. */
export function creatorInsight(rows: CreatorPerformanceRow[]): ReportInsight {
  if (rows.length === 0) {
    return {
      title: 'No creators with work in this period',
      body: 'Creators appear here once they are contracted on a campaign in the period.',
      bullets: [],
    };
  }
  const tiers = new Map<string, number[]>();
  rows.forEach((row) => tiers.set(row.tier, [...(tiers.get(row.tier) ?? []), row.engagementRate]));
  const tierAverages = [...tiers.entries()]
    .map(([tier, rates]) => ({ tier, rate: rates.reduce((sum, rate) => sum + rate, 0) / rates.length }))
    .sort((a, b) => b.rate - a.rate);
  const onTime = rows.reduce((sum, row) => sum + row.onTimeRate, 0) / rows.length;
  const top = [...rows].sort((a, b) => b.engagementRate - a.engagementRate);
  return {
    title: `${tierAverages[0].tier} creators lead the cohort on engagement`,
    body: `${tierAverages.map((entry) => `${entry.tier} ${entry.rate.toFixed(1)}%`).join(' · ')} average engagement. Delivery on time averages ${pct(onTime)} across the cohort.`,
    bullets: top
      .slice(0, 3)
      .map((row) => `${row.name} — ${row.engagementRate.toFixed(1)}% engagement on ${row.campaign}`),
  };
}
